import { DestroyRef, Injectable, Signal, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';
import {
  CurriculumType,
  SubjectCreateOption,
  SubjectTypeOption,
  TeacherOption,
  TeachingTypeOption,
} from 'src/app/services/models/subjects/subject.model';
import { AcademicTermModel } from 'src/app/services/models/study-period/study-period.model';
import {
  ScheduleSubmissionUsersByUserId,
  SubjectManagementCreateFacade,
} from './subject-management-create.facade';

type ReferenceOptionKey = 'studyModes' | 'curriculumTypes' | 'teachingTypes' | 'teachers';

export interface SubjectManagementCreateOptionsState {
  selectedAcademicTerm: AcademicTermModel | null;
  selectedAcademicTermId: number | null;
  subjectTypeOptions: SubjectTypeOption[];
  subjectOptions: SubjectCreateOption[];
  curriculumTypeOptions: CurriculumType[];
  teachingTypeOptions: TeachingTypeOption[];
  teacherOptions: TeacherOption[];
  scheduleSubmissionUsersByUserId: ScheduleSubmissionUsersByUserId;
}

@Injectable()
export class SubjectManagementCreateOptionsStore {
  readonly selectedAcademicTerm: Signal<AcademicTermModel | null>;
  readonly selectedAcademicTermId: Signal<number | null>;
  readonly subjectTypeOptions: Signal<SubjectTypeOption[]>;
  readonly subjectOptions: Signal<SubjectCreateOption[]>;
  readonly curriculumTypeOptions: Signal<CurriculumType[]>;
  readonly teachingTypeOptions: Signal<TeachingTypeOption[]>;
  readonly teacherOptions: Signal<TeacherOption[]>;
  readonly scheduleSubmissionUsersByUserId: Signal<ScheduleSubmissionUsersByUserId>;
  readonly state: Signal<SubjectManagementCreateOptionsState>;

  private readonly facade = inject(SubjectManagementCreateFacade);
  private readonly destroyRef = inject(DestroyRef);

  private readonly selectedAcademicTermState = signal<AcademicTermModel | null>(null);
  private readonly subjectTypeOptionsState = signal<SubjectTypeOption[]>([]);
  private readonly subjectOptionsState = signal<SubjectCreateOption[]>([]);
  private readonly curriculumTypeOptionsState = signal<CurriculumType[]>([]);
  private readonly teachingTypeOptionsState = signal<TeachingTypeOption[]>([]);
  private readonly teacherOptionsState = signal<TeacherOption[]>([]);
  private readonly scheduleSubmissionUsersState = signal<ScheduleSubmissionUsersByUserId>({});

  private academicTermLoadingKey: string | null = null;
  private resolvedAcademicTermKey: string | null = null;
  private subjectOptionsLoadingKey: string | null = null;
  private loadedSubjectOptionsKey: string | null = null;
  private scheduleSubmissionUsersLoadingTermId: number | null = null;
  private loadedScheduleSubmissionUsersTermId: number | null = null;
  private referenceOptionsLoading = new Set<ReferenceOptionKey>();
  private referenceOptionsLoaded = new Set<ReferenceOptionKey>();

  constructor() {
    this.selectedAcademicTerm = this.selectedAcademicTermState.asReadonly();
    this.selectedAcademicTermId = computed(() => this.selectedAcademicTermState()?.id ?? null);
    this.subjectTypeOptions = this.subjectTypeOptionsState.asReadonly();
    this.subjectOptions = this.subjectOptionsState.asReadonly();
    this.curriculumTypeOptions = this.curriculumTypeOptionsState.asReadonly();
    this.teachingTypeOptions = this.teachingTypeOptionsState.asReadonly();
    this.teacherOptions = this.teacherOptionsState.asReadonly();
    this.scheduleSubmissionUsersByUserId = this.scheduleSubmissionUsersState.asReadonly();
    this.state = computed<SubjectManagementCreateOptionsState>(() => ({
      selectedAcademicTerm: this.selectedAcademicTerm(),
      selectedAcademicTermId: this.selectedAcademicTermId(),
      subjectTypeOptions: this.subjectTypeOptions(),
      subjectOptions: this.subjectOptions(),
      curriculumTypeOptions: this.curriculumTypeOptions(),
      teachingTypeOptions: this.teachingTypeOptions(),
      teacherOptions: this.teacherOptions(),
      scheduleSubmissionUsersByUserId: this.scheduleSubmissionUsersByUserId(),
    }));
  }

  loadReferenceOptions(): void {
    this.loadSubjectTypeOptions();
    this.loadCurriculumTypeOptions();
    this.loadTeachingTypeOptions();
    this.loadTeacherOptions();
  }

  resolveAcademicTerm(name: string | null, year: string | null): void {
    const academicTermKey = `${name ?? ''}|${year ?? ''}`;

    if (
      this.academicTermLoadingKey === academicTermKey
      || this.resolvedAcademicTermKey === academicTermKey
    ) {
      return;
    }

    this.academicTermLoadingKey = academicTermKey;
    this.resolvedAcademicTermKey = null;
    this.clearAcademicTermDependentState();

    this.facade.getAcademicTerms(name, year)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (terms) => {
          if (this.academicTermLoadingKey !== academicTermKey) {
            return;
          }

          this.academicTermLoadingKey = null;
          this.resolvedAcademicTermKey = academicTermKey;
          const academicTerm = terms.length > 0 ? terms[0] : null;
          this.selectedAcademicTermState.set(academicTerm);

          if (academicTerm == null) {
            return;
          }

          this.loadScheduleSubmissionUsers(academicTerm.id);
        },
        error: () => {
          if (this.academicTermLoadingKey !== academicTermKey) {
            return;
          }

          this.academicTermLoadingKey = null;
          this.resolvedAcademicTermKey = null;
          this.clearAcademicTermDependentState();
        },
      });
  }

  loadSubjectOptions(studyModeId: number | null): void {
    const academicTermId = this.selectedAcademicTermId();

    if (academicTermId == null || studyModeId == null) {
      this.subjectOptionsState.set([]);
      this.subjectOptionsLoadingKey = null;
      this.loadedSubjectOptionsKey = null;
      return;
    }

    const subjectOptionsKey = `${academicTermId}|${studyModeId}`;

    if (
      this.subjectOptionsLoadingKey === subjectOptionsKey
      || this.loadedSubjectOptionsKey === subjectOptionsKey
    ) {
      return;
    }

    this.subjectOptionsLoadingKey = subjectOptionsKey;
    this.facade.getSubjectOptions(academicTermId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (options) => {
          if (this.subjectOptionsLoadingKey !== subjectOptionsKey) {
            return;
          }

          this.subjectOptionsLoadingKey = null;
          this.loadedSubjectOptionsKey = subjectOptionsKey;
          this.subjectOptionsState.set(options);
        },
        error: () => {
          if (this.subjectOptionsLoadingKey !== subjectOptionsKey) {
            return;
          }

          this.subjectOptionsLoadingKey = null;
          this.loadedSubjectOptionsKey = null;
          this.subjectOptionsState.set([]);
        },
      });
  }

  private loadSubjectTypeOptions(): void {
    this.loadReferenceOption(
      'studyModes',
      () => this.facade.getStudyModeOptions(),
      (options) => this.subjectTypeOptionsState.set(options)
    );
  }

  private loadCurriculumTypeOptions(): void {
    this.loadReferenceOption(
      'curriculumTypes',
      () => this.facade.getCurriculumTypeOptions(),
      (options) => this.curriculumTypeOptionsState.set(options)
    );
  }

  private loadTeachingTypeOptions(): void {
    this.loadReferenceOption(
      'teachingTypes',
      () => this.facade.getTeachingTypeOptions(),
      (options) => this.teachingTypeOptionsState.set(options)
    );
  }

  private loadTeacherOptions(): void {
    this.loadReferenceOption(
      'teachers',
      () => this.facade.getTeacherOptions(),
      (options) => this.teacherOptionsState.set(options)
    );
  }

  private loadReferenceOption<T>(
    key: ReferenceOptionKey,
    request: () => Observable<T[]>,
    setOptions: (options: T[]) => void
  ): void {
    if (this.referenceOptionsLoading.has(key) || this.referenceOptionsLoaded.has(key)) {
      return;
    }

    this.referenceOptionsLoading.add(key);
    request()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (options) => {
          this.referenceOptionsLoading.delete(key);
          this.referenceOptionsLoaded.add(key);
          setOptions(options);
        },
        error: () => {
          this.referenceOptionsLoading.delete(key);
          this.referenceOptionsLoaded.delete(key);
          setOptions([]);
        },
      });
  }

  private loadScheduleSubmissionUsers(academicTermId: number): void {
    if (
      this.scheduleSubmissionUsersLoadingTermId === academicTermId
      || this.loadedScheduleSubmissionUsersTermId === academicTermId
    ) {
      return;
    }

    this.scheduleSubmissionUsersLoadingTermId = academicTermId;
    this.facade.getScheduleSubmissionUsersByUserId(academicTermId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (optionsByUserId) => {
          if (this.scheduleSubmissionUsersLoadingTermId !== academicTermId) {
            return;
          }

          this.scheduleSubmissionUsersLoadingTermId = null;
          this.loadedScheduleSubmissionUsersTermId = academicTermId;
          this.scheduleSubmissionUsersState.set(optionsByUserId);
        },
        error: () => {
          if (this.scheduleSubmissionUsersLoadingTermId !== academicTermId) {
            return;
          }

          this.scheduleSubmissionUsersLoadingTermId = null;
          this.loadedScheduleSubmissionUsersTermId = null;
          this.scheduleSubmissionUsersState.set({});
        },
      });
  }

  private clearAcademicTermDependentState(): void {
    this.selectedAcademicTermState.set(null);
    this.subjectOptionsState.set([]);
    this.scheduleSubmissionUsersState.set({});
    this.subjectOptionsLoadingKey = null;
    this.loadedSubjectOptionsKey = null;
    this.scheduleSubmissionUsersLoadingTermId = null;
    this.loadedScheduleSubmissionUsersTermId = null;
  }
}
